import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { CompanyDetails, type CompanyDetails as Details } from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useRequest } from '../lib/use-request';

const FIELDS: { key: keyof Details; label: string; hint: string }[] = [
  { key: 'companyName', label: 'Company name', hint: 'Exactly as registered at Companies House.' },
  { key: 'companyNumber', label: 'Company number', hint: '8 digits, or 2 letters and 6 digits.' },
  { key: 'registeredOffice', label: 'Registered office', hint: 'The address on the Companies House register.' },
  { key: 'icoNumber', label: 'ICO registration number', hint: 'Leave empty until the data protection fee is paid.' },
  { key: 'supportEmail', label: 'Support email', hint: 'Shown to players for help and age reviews.' },
  { key: 'privacyEmail', label: 'Privacy email', hint: 'Shown in the Privacy Policy for data requests.' },
];

// super admin only: who runs London Life, as shown in the legal documents. Saving asks for a fresh
// two-step code and is recorded in the audit log.
export function CompanyPage() {
  const current = useRequest(useCallback(() => api.admin.companyDetails(), []));
  const [form, setForm] = useState<Details | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (current.data) setForm(current.data.company);
  }, [current.data]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form) return;
    setSaved(false);
    const parsed = CompanyDetails.safeParse({
      ...form,
      icoNumber: form.icoNumber?.trim() ? form.icoNumber.trim() : null,
    });
    if (!parsed.success) return current.fail(new Error(parsed.error.issues[0]?.message));
    try {
      setForm((await api.admin.saveCompanyDetails(parsed.data)).company);
      setSaved(true);
    } catch (problem) {
      current.fail(problem);
    }
  };
  return (
    <section>
      <PageHeader
        title="Company details"
        description="Who runs London Life, as shown to players in the Terms, Privacy Policy and Disclaimer."
      />
      {current.error && <ErrorNotice error={current.error} onRetry={() => void current.reload()} />}
      {form && (
        <form className="admin-panel admin-form" onSubmit={(event) => void submit(event)}>
          {FIELDS.map((field) => (
            <div className="admin-field" key={field.key}>
              <Label htmlFor={`company-${field.key}`}>{field.label}</Label>
              <Input
                id={`company-${field.key}`}
                value={form[field.key] ?? ''}
                onChange={(event) => {
                  setSaved(false);
                  setForm({ ...form, [field.key]: event.target.value });
                }}
              />
              <p className="admin-muted">{field.hint}</p>
            </div>
          ))}
          <div className="admin-page-actions">
            <Button type="submit">Save company details</Button>
            {saved && (
              <span role="status" className="admin-muted">
                Saved. The legal pages show the new details straight away.
              </span>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
